import React from 'react';
import { Users, TrendingUp, DollarSign, Target, CheckCircle, UserCheck } from 'lucide-react';
import Icon from '../../../components/AppIcon';


interface UserKPICardsProps {
  totalUsers: number;
  avgAge: number;
  avgSavingsPerUser: number;
  totalSavings: number;
  avgConversionRate: number;
  filteredCount: number;
  activatedUsers?: number;
  activationRate?: number;
}

const UserKPICards: React.FC<UserKPICardsProps> = ({
  totalUsers,
  avgAge,
  avgSavingsPerUser,
  totalSavings,
  avgConversionRate,
  filteredCount,
  activatedUsers = 0,
  activationRate = 0
}) => {
  const kpiData = [
    {
      title: 'Total Users',
      value: totalUsers.toLocaleString(),
      icon: Users,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      description: 'Platform registered users',
      trend: filteredCount < totalUsers ? `${filteredCount} filtered` : undefined
    },
    {
      title: 'Activated Users',
      value: activatedUsers.toLocaleString(),
      icon: UserCheck,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      description: 'Users with QR activation',
      trend: `${activationRate.toFixed(1)}% activation rate`
    },
    {
      title: 'Average Age',
      value: avgAge.toString(),
      icon: TrendingUp,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      description: 'User demographic average',
      trend: undefined
    },
    {
      title: 'Avg Savings/User',
      value: `€${avgSavingsPerUser.toFixed(0)}`,
      icon: DollarSign,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      description: 'Per user savings metric',
      trend: undefined
    },
    {
      title: 'Total Savings',
      value: `€${totalSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: CheckCircle,
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      description: 'Platform-wide savings',
      trend: undefined
    },
    {
      title: 'Avg Conversion',
      value: `${avgConversionRate.toFixed(1)}%`,
      icon: Target,
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      description: 'User engagement rate',
      trend: undefined
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
      {kpiData.map((kpi) => {
        const Icon = kpi.icon;
        return (
          <div 
            key={kpi.title}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${kpi.bgColor} p-3 rounded-lg`}>
                <Icon className={`h-6 w-6 ${kpi.iconColor}`} />
              </div>
            </div>
            
            <h3 className="text-sm font-medium text-gray-600 mb-1">
              {kpi.title}
            </h3>
            
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl font-bold text-gray-900">
                {kpi.value}
              </p>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              {kpi.description}
            </p>
            
            {kpi.trend && (
              <p className="text-xs text-blue-600 font-medium mt-1">
                {kpi.trend}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UserKPICards;